// I.3 — Admin Contract Rates page (platform-admin / forwarder writes).
//
// Rate-card key: customer_tenant_id + directional pickup_postal +
// directional dropoff_postal + vehicle_type + load_type
// (FTL/LTL/FCL/LCL/etc.).
//
// Zero tolerance: when an LR matches a card but its `elfogadott_dij`
// differs from `rate_amount`, intake forces `waiting_for_approval` and
// requires a deviation_justification. Surfaced in the deal history.
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "axios";
import { ScrollText, Plus, Trash2, Save, Loader2, AlertCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contract-rates`;
const mono = (s, c, w, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: s,
  color: c || "var(--text, #f0f2f8)",
  fontWeight: w || 400,
  letterSpacing: ls,
});
const syne = (s, w, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: s,
  fontWeight: w || 700,
  letterSpacing: ls,
});

const LOAD_TYPES = ["FTL", "LTL", "FCL", "LCL", "TRACTOR_ONLY", "GROUPAGE"];

const useContractRates = (customerFilter) =>
  useQuery({
    queryKey: ["contract-rates", customerFilter || "_all"],
    queryFn: () => axios.get(`${API}${customerFilter ? `?customer_tenant_id=${customerFilter}` : ""}`).then((r) => r.data),
    staleTime: 30_000,
  });

const useCreateRate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(API, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contract-rates"] }),
  });
};
const useDeleteRate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid) => axios.delete(`${API}/${rid}`).then(() => rid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contract-rates"] }),
  });
};

const _inp = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#f0f2f8",
  ...mono(11),
  padding: "5px 8px",
  borderRadius: 4,
  width: "100%",
};

export default function AdminContractRates() {
  const [filter, setFilter] = useState("");
  const { data, isLoading, error } = useContractRates(filter);
  const createRate = useCreateRate();
  const deleteRate = useDeleteRate();

  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState({
    customer_tenant_id: "", pickup_postal: "", dropoff_postal: "",
    vehicle_type: "", load_type: "FTL", rate_amount: "",
    currency: "EUR", valid_from: "", valid_to: "", note: "",
  });

  const submit = async () => {
    const body = {
      customer_tenant_id: draft.customer_tenant_id.trim(),
      pickup_postal: draft.pickup_postal.trim(),
      dropoff_postal: draft.dropoff_postal.trim(),
      vehicle_type: draft.vehicle_type.trim(),
      transport_mode: draft.load_type.trim(),  // backend key is `transport_mode` (carry user vocabulary)
      rate_amount: Number(draft.rate_amount),
      currency: draft.currency,
      valid_from: draft.valid_from || null,
      valid_to: draft.valid_to || null,
      note: draft.note || null,
    };
    if (!body.customer_tenant_id || !body.pickup_postal || !body.dropoff_postal ||
        !body.vehicle_type || !body.transport_mode || !body.rate_amount) {
      toast.error("Fill customer + pickup + dropoff + vehicle + load type + rate");
      return;
    }
    try {
      await createRate.mutateAsync(body);
      toast.success("Rate card created");
      setNewOpen(false);
      setDraft({ ...draft, customer_tenant_id: "", pickup_postal: "", dropoff_postal: "",
                 vehicle_type: "", rate_amount: "", note: "" });
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || det?.code || "Create failed");
    }
  };

  if (error?.response?.status === 403) {
    return (
      <div data-testid="contract-rates-403" className="rounded-lg p-6" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={16} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>Contract rates are forwarder-internal only. Your account doesn't have access.</span>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <div data-testid="admin-contract-rates-page">
      <header className="mb-3">
        <h1 style={syne("clamp(22px, 3vw, 28px)", 800, "-0.5px")} data-testid="view-title">
          <ScrollText size={20} className="inline mr-2" />
          Contract Rates
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          One rate per <span style={{ color: "#9ec3ec" }}>customer + directional pickup→dropoff postal + vehicle type + load type</span>. Direction matters — DE→HU and HU→DE are separate cards. <strong style={{ color: "#ef4444" }}>Zero tolerance:</strong> if a deal's accepted price doesn't match exactly, it's force-routed to approval with mandatory justification.
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="contract-rates-toolbar">
        <input
          type="text"
          placeholder="Filter by customer_tenant_id (optional)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ ..._inp, width: 320 }}
          data-testid="contract-rates-filter"
        />
        <span style={mono(10, "#94a3b8")}>{items.length} card{items.length === 1 ? "" : "s"}</span>
        <button
          onClick={() => setNewOpen(true)}
          className="ml-auto px-3 py-1.5 rounded inline-flex items-center gap-1"
          style={{ background: "#0a8754", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }}
          data-testid="contract-rates-new-btn"
        >
          <Plus size={11} /> New rate card
        </button>
      </div>

      {newOpen && (
        <div className="rounded-lg p-3 mb-3" style={{ background: "rgba(10,135,84,0.05)", border: "1px solid rgba(10,135,84,0.30)" }} data-testid="contract-rates-new-form">
          <div className="grid grid-cols-4 gap-2 mb-2">
            <input type="text" placeholder="Customer tenant id *" value={draft.customer_tenant_id} onChange={(e) => setDraft({ ...draft, customer_tenant_id: e.target.value })} style={_inp} data-testid="contract-rates-new-customer" />
            <input type="text" placeholder="Pickup postal *" value={draft.pickup_postal} onChange={(e) => setDraft({ ...draft, pickup_postal: e.target.value })} style={_inp} data-testid="contract-rates-new-pickup" />
            <input type="text" placeholder="Dropoff postal *" value={draft.dropoff_postal} onChange={(e) => setDraft({ ...draft, dropoff_postal: e.target.value })} style={_inp} data-testid="contract-rates-new-dropoff" />
            <input type="text" placeholder="Vehicle (e.g. MEGA, 12t, AIR, SEA) *" value={draft.vehicle_type} onChange={(e) => setDraft({ ...draft, vehicle_type: e.target.value })} style={_inp} data-testid="contract-rates-new-vehicle" />
          </div>
          <div className="grid grid-cols-5 gap-2 mb-2">
            <select value={draft.load_type} onChange={(e) => setDraft({ ...draft, load_type: e.target.value })} style={_inp} data-testid="contract-rates-new-load-type">
              {LOAD_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Rate amount *" value={draft.rate_amount} onChange={(e) => setDraft({ ...draft, rate_amount: e.target.value })} style={_inp} data-testid="contract-rates-new-rate" />
            <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} style={_inp} data-testid="contract-rates-new-currency">
              {["EUR", "HUF", "USD", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="date" placeholder="Valid from" value={draft.valid_from} onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })} style={_inp} data-testid="contract-rates-new-valid-from" />
            <input type="date" placeholder="Valid to" value={draft.valid_to} onChange={(e) => setDraft({ ...draft, valid_to: e.target.value })} style={_inp} data-testid="contract-rates-new-valid-to" />
          </div>
          <div className="mb-2">
            <input type="text" placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} style={_inp} data-testid="contract-rates-new-note" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={createRate.isPending} className="px-3 py-1.5 rounded inline-flex items-center gap-1" style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="contract-rates-new-submit">
              <Save size={11} /> {createRate.isPending ? "Saving…" : "Create"}
            </button>
            <button onClick={() => setNewOpen(false)} className="px-2.5 py-1.5 rounded inline-flex items-center gap-1" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="contract-rates-new-cancel">
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading rate cards…</div>}

      <div className="rounded-lg overflow-x-auto" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="contract-rates-list">
        <table style={{ width: "100%", borderCollapse: "collapse", ...mono(11) }}>
          <thead>
            <tr style={{ background: "rgba(0,0,0,0.30)" }}>
              {["Customer", "Pickup", "→", "Dropoff", "Vehicle", "Load", "Rate", "Validity", ""].map((h, i) => (
                <th key={i} style={{ padding: "8px 10px", textAlign: i === 6 ? "right" : "left", ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !isLoading && (
              <tr><td colSpan={9} style={{ padding: 16, textAlign: "center", ...mono(11, "#94a3b8") }}>No rate cards yet.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid={`contract-rates-row-${r.id}`}>
                <td style={{ padding: "6px 10px", ...mono(11, "#9ec3ec", 700) }}>{r.customer_tenant_id?.substring(0, 16)}{r.customer_tenant_id?.length > 16 ? "…" : ""}</td>
                <td style={{ padding: "6px 10px" }}>{r.pickup_postal}</td>
                <td style={{ padding: "6px 4px", ...mono(11, "#94a3b8", 700) }}>→</td>
                <td style={{ padding: "6px 10px" }}>{r.dropoff_postal}</td>
                <td style={{ padding: "6px 10px" }}>{r.vehicle_type}</td>
                <td style={{ padding: "6px 10px", ...mono(10, "#c5cad8") }}>{r.transport_mode}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", ...mono(11, "#0a8754", 700) }}>{r.currency === "HUF" ? `${Number(r.rate_amount).toLocaleString()} Ft` : `€ ${Number(r.rate_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                <td style={{ padding: "6px 10px", ...mono(10, "#94a3b8") }}>{(r.valid_from || "—") + " → " + (r.valid_to || "open")}</td>
                <td style={{ padding: "6px 10px" }}>
                  <button onClick={() => { if (window.confirm("Delete this rate card?")) deleteRate.mutate(r.id); }} className="p-1 rounded" style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.40)", color: "#b22a29", cursor: "pointer" }} data-testid={`contract-rates-delete-${r.id}`}>
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
